import { withStyles, Grid, Typography, Paper } from '@material-ui/core';
import React from 'react'; // eslint-disable-line
import styles, { StyledComponent } from '../../../../../styles/styles';
import { LabeledValue } from '../../../../../controls/labeled-value';
import { connect } from 'react-redux';
import { State } from '../../../../../redux/root-reducer';
import { FormDetails, getFormDetails } from '../form-details-reducer';
import { getDisplayDate } from '../../../../../shared-functions';
import { OpenModal, openModalAction } from '../../../../../form-modal/actions';
import { UpdateFormDetails, updateFormDetailsAction } from '../actions/update-form-details';
import { getClaim } from '../../../../../claim';
import { Nullable } from '../../../../../shared-types';
import { ClaimType } from '../../../../../claim/claim-types';

interface BaseProps {
    formDetails?: FormDetails;
    disableEditButton?: boolean;
    updateFormDetails?: UpdateFormDetails;
    type: string;
    claimType?: Nullable<ClaimType>;
}

interface Props extends BaseProps, StyledComponent {
    readonly openModal: OpenModal;
}

const personalDetailsView: React.FunctionComponent<Props> = ({
    classes,
    openModal,
    formDetails,
    updateFormDetails,
    type,
    disableEditButton,
    claimType,
}: Props) => {
    let policyHolderPersonalDetails = formDetails?.lifeAssured?.personalDetails;
    const companyDetails = formDetails?.policyholder?.individual?.companyDetails;

    if (type !== 'LifeAssured') {
        policyHolderPersonalDetails = formDetails?.policyholder?.individual?.personalDetails;
    }
    const personalDetails = policyHolderPersonalDetails;

    const isThePolicyHolderAnIndividualOrCompany =
        formDetails?.policyholder?.isThePolicyHolderAnIndividualOrCompany;

    const policyHolderType = isThePolicyHolderAnIndividualOrCompany;

    const isLifeAssuredSameAsPolicyHolder =
        formDetails?.lifeAssured?.personalDetails?.isTheLifeAssuredTheSameAsPolicyHolder;
    const relationshipToPolicyHolder =
        formDetails?.lifeAssured?.personalDetails?.relationshipToPolicyHolder;

    const capitalizeFirstLetter = (str?: string | null) =>
        !str ? '' : str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();

    if (isThePolicyHolderAnIndividualOrCompany === 'Company') {
        return (
            <React.Fragment>
                <Grid container justify="space-between" className={classes.marginTop2}>
                    <Grid container item xs={10} alignItems="center">
                        <Typography
                            style={{ padding: 10 }}
                            className={classes.heading}
                            variant="body1"
                        >
                            Company Details
                        </Typography>
                    </Grid>
                </Grid>
                <Paper className={classes.paper}>
                    <Grid container justify="flex-start">
                        <Grid item xs={12} container justify="flex-start">
                            <Grid item xs={12} lg={12}>
                                <LabeledValue
                                    label="Is the policyholder an individual or company?"
                                    value={isThePolicyHolderAnIndividualOrCompany}
                                />
                            </Grid>
                            <Grid item xs={4} lg={3}>
                                <LabeledValue
                                    label="Company Trading Name"
                                    value={
                                        companyDetails?.companyTradingName ||
                                        companyDetails?.phCompanyName ||
                                        companyDetails?.entityName
                                    }
                                />
                            </Grid>

                            <Grid item xs={4} lg={3}>
                                <LabeledValue
                                    label="Company Registration Number"
                                    value={
                                        companyDetails?.companyRegistrationNumber ||
                                        companyDetails?.phCompanyRegistrationNumber ||
                                        companyDetails?.entityRegistrationNumber
                                    }
                                />
                            </Grid>
                            {companyDetails && (
                                <Grid item xs={4} lg={3}>
                                    <LabeledValue
                                        label="Company Registration Date"
                                        value={getDisplayDate(
                                            companyDetails?.companyRegistrationDate ||
                                                companyDetails?.dateOfIncorporation
                                        )}
                                    />
                                </Grid>
                            )}
                            <Grid item xs={4} lg={3}>
                                <LabeledValue
                                    label="Country of incorporation"
                                    value={
                                        companyDetails?.countryOfCompany ||
                                        companyDetails?.countryOfIncorporation ||
                                        companyDetails?.phCountryOfIncorporation ||
                                        companyDetails?.entityProvince
                                    }
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                </Paper>
            </React.Fragment>
        );
    }

    return (
        <React.Fragment>
            <Grid container justify="space-between" className={classes.marginTop2}>
                <Grid container item xs={10} alignItems="center">
                    <Typography style={{ padding: 10 }} className={classes.heading} variant="body1">
                        Personal Details
                    </Typography>
                </Grid>
            </Grid>
            <Paper className={classes.paper}>
                <Grid container spacing={2}>
                    {type !== 'LifeAssured' && (
                        <Grid item xs={12}>
                            <LabeledValue
                                label="Is the policyholder an individual or company"
                                value={policyHolderType}
                            />
                        </Grid>
                    )}
                    {type === 'LifeAssured' && (
                        <Grid item xs={12}>
                            <LabeledValue
                                label="Is the life assured the same as policyholder?"
                                value={isLifeAssuredSameAsPolicyHolder}
                            />
                        </Grid>
                    )}

                    {type === 'LifeAssured' && isLifeAssuredSameAsPolicyHolder === 'No' && (
                        <Grid item xs={12} sm={6} md={4} lg={3}>
                            <LabeledValue
                                label="Relationship to policyholder?"
                                value={relationshipToPolicyHolder}
                            />
                        </Grid>
                    )}

                    <Grid item xs={12} sm={6} md={4} lg={3}>
                        <LabeledValue
                            label="Title"
                            value={capitalizeFirstLetter(personalDetails?.title)}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6} md={4} lg={3}>
                        <LabeledValue
                            label="Full first names"
                            value={personalDetails?.fullFirstNames}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6} md={4} lg={3}>
                        <LabeledValue label="Surname" value={personalDetails?.surname} />
                    </Grid>

                    {type !== 'LifeAssured' && (
                        <Grid item xs={12} sm={6} md={4} lg={3}>
                            <LabeledValue
                                label="Cellphone number"
                                value={personalDetails?.cellphoneNumber}
                            />
                        </Grid>
                    )}

                    {type !== 'LifeAssured' && (
                        <Grid item xs={12} sm={6} md={4} lg={3}>
                            <LabeledValue
                                label="Email Address"
                                value={personalDetails?.emailAddress}
                            />
                        </Grid>
                    )}

                    <Grid item xs={12} sm={6} md={4} lg={3}>
                        <LabeledValue
                            label="Has your marital status changed since you took up policy?"
                            value={
                                personalDetails?.hasYourMaritalStatusChangedSinceYouTookUpPolicy ??
                                personalDetails?.hasYourMaritalStatusChangedSinceYouTookUpThePolicy
                            }
                        />
                    </Grid>

                    <Grid item xs={12} sm={6} md={4} lg={3}>
                        <LabeledValue
                            label="Marital Status"
                            value={personalDetails?.maritalStatus}
                        />
                    </Grid>

                    {personalDetails?.dateOfMarriage && (
                        <Grid item xs={12} sm={6} md={4} lg={3}>
                            <LabeledValue
                                label="Date of marriage"
                                value={getDisplayDate(personalDetails?.dateOfMarriage)}
                            />
                        </Grid>
                    )}

                    {personalDetails?.dateOfDivorce && (
                        <Grid item xs={12} sm={6} md={4} lg={3}>
                            <LabeledValue
                                label="Date of divorce"
                                value={getDisplayDate(personalDetails?.dateOfDivorce)}
                            />
                        </Grid>
                    )}

                    <Grid item xs={12} sm={6} md={4} lg={3}>
                        <LabeledValue
                            label="Are you a South African resident"
                            value={personalDetails?.areYouASouthAfricanResident}
                        />
                    </Grid>

                    {personalDetails?.idNumber && (
                        <Grid item xs={12} sm={6} md={4} lg={3}>
                            <LabeledValue label="ID Number" value={personalDetails?.idNumber} />
                        </Grid>
                    )}

                    {personalDetails?.passportNumber && (
                        <Grid item xs={12} sm={6} md={4} lg={3}>
                            <LabeledValue
                                label="Passport Number"
                                value={personalDetails?.passportNumber}
                            />
                        </Grid>
                    )}
                </Grid>
            </Paper>
        </React.Fragment>
    );
};

const mapStateToProps = (state: State, ownProps: BaseProps) => ({
    formDetails: ownProps.formDetails ?? getFormDetails(state),
    disableEditButton: ownProps.disableEditButton ?? false,
    updateFormDetails: ownProps.updateFormDetails ?? updateFormDetailsAction,
    claimType: getClaim(state).claimType,
});

const mapDispatchToProps = {
    openModal: openModalAction,
    updateFormDetails: updateFormDetailsAction,
};

export const PersonalDetailsView = connect(
    mapStateToProps,
    mapDispatchToProps
)(withStyles(styles)(personalDetailsView));
